// components/SearchSection.jsx
import React, { useState } from "react";
import SearchIcon       from "@mui/icons-material/Search";
import AbcIcon          from "@mui/icons-material/Abc";
import ChatBubbleIcon   from "@mui/icons-material/ChatBubble";
import TuneIcon         from "@mui/icons-material/Tune";
import MusicNoteIcon    from "@mui/icons-material/MusicNote";
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import LyricsOutlinedIcon from '@mui/icons-material/LyricsOutlined';
import { Reveal }       from "../lib/hooks";
import { WF_HEIGHTS }   from "../lib/data";
import Helper from "../../../services/helper";

function WaveformDemo() {
  const [bpm, setBpm]       = useState(false);
  const [vocals, setVocals] = useState(true);

  return (
    <div className="border-pink-200 rounded-xl p-[18px]"
      style={{background : "rgb(var(--canvas))"}}>
      {/* Track info */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-20 h-20 rounded-md flex-shrink-0 bg-gradient-to-br from-blue-900 to-pink-500" />
        <div>
          <div className="lic-step-title">Ruhun Dansi</div>
          <div className="text-xs navy-text-1">Aiden Stonewall · 100 BPM · 02:37</div>
        </div>
      </div>

      {/* Waveform */}
      <div className="relative mb-2.5 w-full overflow-x-auto">
        <div className="flex items-center gap-0.5 h-28">
          {WF_HEIGHTS.map((h, i) => {
            const p = i / WF_HEIGHTS.length;
            const cls = p > 0.28 && p < 0.48
              ? "bg-green-600"
              : p > 0.48
              ? "bg-green-700"
              : "bg-green-800";
            return (
              <div
                key={i}
                className={`w-3 rounded-xl ${cls}`}
                style={{ height: `${h}%` }}
              />
            );
          })}
        </div>
        {/* Selection region */}
        <div className="absolute top-0 bottom-0 left-1/4 right-1/3 bg-pink-500 bg-opacity-30 rounded-2xl pointer-events-none">
          <div className="absolute top-1/2 -left-0 w-3.5 h-3.5 bg-pink-500 rounded-full border-2 border-pink-100" />
          <div className="absolute top-1/2 -right-0 w-3.5 h-3.5 bg-pink-500 rounded-full border-2 border-pink-100" />
        </div>
      </div>

      {/* Toggle chips */}
      <div className="flex gap-2 mt-2">
        {[
          { key: "bpm",    label: "Prioritise BPM",  state: bpm,    set: setBpm },
          { key: "vocals", label: "Ignore Vocals",   state: vocals,  set: setVocals },
        ].map(({ key, label, state, set }) => (
          <button
            key={key}
            onClick={() => set(s => !s)}
            className={`
              flex items-center text-xs font-medium
              px-2.5 py-2 rounded-full
              border transition-all duration-200 cursor-pointer
              font-dm
              ${state
                ? "bg-pink-100 border-pink-300 pink-text"
                : "navy-text border-blue-900"
              }
            `}
          >
            <span className={`
              w-2 h-2 rounded-full border border-pink-200 transition-all m-2
              ${state ? "badge-dot blink" : "bg-[rgba(27,58,107,0.2)]"}
            `} />
            <span className="ml-2">{label}&nbsp;&nbsp;&nbsp;</span>
          </button>
        ))}
      </div>
      <p className="text-xs navy-text-1 text-center mt-2 italic">
        Drag handles · Select any section · Click Search
      </p>
    </div>
  );
}

const SEARCH_CARDS = [
  {
    pill: "Similarity Search",
    pillStyle: "bg-pink/10 text-pink border-pink/20",
    // icon: <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30">
    //         <circle cx="9" cy="10" r="5.5" fill="none" stroke="#767577" strokeWidth="1.8"/>
    //         <circle cx="14" cy="10" r="5.5" fill="none" stroke="#767577" strokeWidth="1.8"/>
    //         <line x1="17.5" y1="15.5" x2="21" y2="19" stroke="#767577" strokeWidth="2" strokeLinecap="round"/>
    //       </svg>,
    // icon: <JoinInnerIcon sx={{ fontSize: 28, color: "#767577" }} />,
    icon: [Helper.getSearchIcon()],
    title: "Use music to find music",
    body: (
      <>
        Paste a link from <strong>YouTube, Spotify, Apple Music, Vimeo, or SoundCloud</strong> — or upload a mp3 or WAV file — and MAYA instantly finds music across the full catalogue that matches the sound and feel of your reference.
        <br /><br />
        Use the <strong>similarity icon</strong> next to a track to pin the exact section — a bridge, chorus, hook, or solo. Toggle <strong>Ignore Vocals</strong> to match on instrumental only, or <strong>Prioritise BPM</strong> to lock the tempo.
      </>
    ),
    hero: true,
    isNew: false,
  },
  {
    pill: "Keyword Search",
    pillStyle: "bg-pink/10 text-pink border-pink/20",
    icon: [<AbcIcon sx={{ fontSize: 28, color: "#767577" }} />],
    title: "Search by track, album, or artist name",
    body: (
      <>
        Already know what you want? Type it in — direct, precise, no noise.
        <br /><br />
        Once you've found your track, let MAYA take it further through <strong>Similarity Search</strong> and <strong>Prompt Search</strong>.
      </>
    ),
    hero: false,
    isNew: false,
  },
  {
    pill: "Prompt & Lyrics",
    pillStyle: "bg-teal/10 text-teal border-teal/20",
    icon: [<ChatBubbleIcon sx={{ fontSize: 28, color: "#767577" }} />, <LyricsOutlinedIcon sx={{ fontSize: 28, color: "#767577" }} />],
    title: "Describe what you need",
    body: (
      <>
        Describe a scene, a feeling, a full creative brief, or whatever comes to mind. MAYA understands intent and context.
        <br /><br />
        Search lyrics by words — 'home,' 'rise,' 'road,' 'together' — or by meaning: <em>'Songs about overcoming adversity and finding freedom'</em>
        <br />
      </>
    ),
    hero: false,
    isNew: true,
  },
];

export default function SearchSection() {
  return (
    <section className="relative overflow-hidden" style={{background : "rgb(var(--canvas))"}}>
      <div className="absolute inset-0" />
      <div className="relative section-inner">
        <Reveal>
          <div className="section-label">MAYA - YOUR PERSONAL SYNC ASSISTANT</div>
          <h2 className="section-title">Find your track<br />in 60 seconds</h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-4 sm:grid-cols-1">
          {SEARCH_CARDS.map((card, i) => (
            <Reveal key={card.pill} delay={i * 70} className={card.hero ? "col-span-2 max-sm:col-span-1" : "col-span-2 sm:col-span-1"}>
              <div className={`
                bg-white dark:bg-[#161b22]
                border border-[rgb(var(--navy))] 
                rounded-xl p-7
                relative overflow-hidden
                transition-all duration-300 ease-[cubic-bezier(.4,0,.2,1)]
                group s-card h-full
                hover:-translate-y-1 hover:border-pink-300
                hover:before:opacity-100
                before:content-[''] before:absolute before:-inset-[2px] before:bg-gradient-to-br
                before:from-pink-100 before:to-pink-300 before:rounded-xl
                before:opacity-0 before:transition-opacity before:duration-300 before:z-[-1]
                ${card.hero ? "grid lg:grid-cols-2 gap-4 sm:grid-cols-1 items-center bg-gradient-to-br from-[rgb(var(--pink))] to-[rgb(var(--canvas))] dark:to-[#161b22] border-pink-200" : ""}
              `}>
                <div>
                  {/* Icon */}
                  <div className="flex gap-2">
                  {card.icon?.map(e => <div className="
                    w-12 h-12 bg-pink-100 border border-pink-200 rounded-xl px-1
                    flex items-center justify-center mb-4
                    transition-all duration-300 group-hover:bg-pink-200 group-hover:scale-105
                  ">
                    {e}
                  </div>)}
                  </div>
                  {/* Pill */}
                  <div className="mb-2">
                  <span className={`
                    text-xs lic-pill mb-2
                  `}>
                    {card.pill}
                  </span>
                  </div>
                  {/* Title */}
                  <h3 className="lic-step-title mb-2">
                    {card.title}
                  </h3>
                  {/* Body */}
                  <p className="lic-step-desc">
                    {card.body}
                  </p>
                </div>
                {/* Hero card gets waveform on the right */}
                {card.hero && (
                  <div className="self-center w-full overflow-x-auto">
                    <WaveformDemo />
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
